

import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  IconButton,
  Collapse,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import useGetData from '../../Hooks/useGetData';
import { useSnackbar } from '../../Context/SnackbarProvider';
import apiClient from '../../Services/ApiClient';
import { COLORS } from '../../Styles/Colors';
import { CancelButton, SubmitButton } from '../../Styles/AppStyles';
import useGetCustomData from '../../Hooks/useGetCustomData';

interface EmailFormProps {
  userId: number;
  onCancel: () => void;
}

const EmailForm: React.FC<EmailFormProps> = ({ userId, onCancel }) => {
  const [emails, loadingEmail, errorEmail, refetch] = useGetData<any>(`/api/emails/subscriptions/${userId}`);
  const [ovsUser, loadingOvsUser] = useGetCustomData(`/api/location/user/ovs/${userId}`);

  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionStates, setSubscriptionStates] = useState<Record<number, string>>({});
  const [selectedLocations, setSelectedLocations] = useState<Record<number, string[]>>({});
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (emails) {
      const initialStates = emails.reduce((acc, email) => {
        acc[email.id] = email.selected_flag;
        return acc;
      }, {} as Record<number, string>);
      setSubscriptionStates(initialStates);
      
      const initialLocations = emails.reduce((acc, email) => {
        acc[email.id] = (email.locations || []).map((loc: any) => String(loc));
        return acc;
      }, {} as Record<number, string[]>);
      setSelectedLocations(initialLocations);
    }
  }, [emails]);

  const handleLocationToggle = (emailId: number, locationId: string) => {
    const locationIdStr = String(locationId);
    const currentLocations = selectedLocations[emailId] || [];
    
    if (currentLocations.includes(locationIdStr) && currentLocations.length === 1) {
      showSnackbar('At least one location must be selected for subscribed emails', 'warning');
      return;
    }
    
    setSelectedLocations(prev => {
      const current = prev[emailId] || [];
      const updated = current.includes(locationIdStr)
        ? current.filter(id => id !== locationIdStr)
        : [...current, locationIdStr];
      return { ...prev, [emailId]: updated };
    });
  };

  const handleExpandToggle = (emailId: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [emailId]: !prev[emailId]
    }));
  };

  const getLocationNames = (emailId: number) => {
    const currentSelectedLocations = selectedLocations[emailId] || [];
    return availableLocations
      .filter((loc: any) => currentSelectedLocations.includes(String(loc.OVID)))
      .map((loc: any) => loc.name);
  };

  const handleSaveSubscriptions = async () => {
    try {
      const invalidSubscriptions = Object.entries(subscriptionStates)
        .filter(([id, flag]) => flag === '1' && (!selectedLocations[Number(id)] || selectedLocations[Number(id)].length === 0))
        .map(([id]) => emails?.find(email => email.id === Number(id))?.name)
        .filter(Boolean);

      if (invalidSubscriptions.length > 0) {
        showSnackbar(`Please select at least one location for: ${invalidSubscriptions.join(', ')}`, 'warning');
        return;
      }

      const changedSubscriptions = Object.entries(subscriptionStates)
        .filter(([id, selected_flag]) => {
          const original = emails?.find(email => email.id === Number(id));
          const originalLocations = (original?.locations || []).map((loc: any) => String(loc));
          const currentLocations = selectedLocations[Number(id)] || [];
          const locationsChanged = JSON.stringify(originalLocations.sort()) !== JSON.stringify(currentLocations.sort());
          return original && (original.selected_flag !== selected_flag || locationsChanged);
        })
        .map(([id, selected_flag]) => {
          const emailId = Number(id);
          const locations = selectedLocations[emailId] || [];
          
          const payload: any = {
            email_id: emailId,
            selected_flag: selected_flag
          };
          
          if (locations.length > 0 && selected_flag === '1') {
            payload.location = locations;
          }
          
          return {
            payload
          };
        });

      setIsLoading(true);

      await apiClient(`/api/emails/subscriptions/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changedSubscriptions),
      });

      setIsLoading(false);
      showSnackbar('Subscriptions updated successfully!', "success");
      refetch();
    } catch (error) {
      showSnackbar('Failed to update subscriptions', "error");
    }
  };

  const availableLocations = ovsUser || [];

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 500, color: COLORS.teal }}>
          Email Subscriptions
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <CancelButton type="button" onClick={onCancel}>Cancel</CancelButton>
          <SubmitButton onClick={handleSaveSubscriptions} disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </SubmitButton>
        </Box>
      </Box>
      
      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 1, mb: 3 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Subscribed</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Locations</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emails && emails.length > 0 ? (
              emails.map((email) => {
                const currentSelectedLocations = selectedLocations[email.id] || [];
                const isExpanded = expandedRows[email.id];
                const selectedLocationNames = getLocationNames(email.id);
                
                return (
                  <React.Fragment key={email.id}>
                    <TableRow 
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: '#f9f9f9',
                          transition: 'background-color 0.2s'
                        } 
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{email.name}</TableCell>
                      <TableCell>{email.description}</TableCell>
                      <TableCell>
                        <Switch
                          sx={{ '& .MuiSwitch-thumb': { backgroundColor: COLORS.teal } }}
                          checked={subscriptionStates[email.id] === '1'}
                          onChange={() => {
                            setSubscriptionStates(prev => {
                              const nextFlag = prev[email.id] === '1' ? '0' : '1';

                              if (nextFlag === '0') {
                                setSelectedLocations(locPrev => ({
                                  ...locPrev,
                                  [email.id]: []
                                }));
                              }
                              else if (nextFlag === '1') {
                                const allLocationIds = availableLocations.map((loc: any) => String(loc.OVID));
                                setSelectedLocations(locPrev => ({
                                  ...locPrev,
                                  [email.id]: allLocationIds
                                }));
                              }

                              return {
                                ...prev,
                                [email.id]: nextFlag
                              };
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {subscriptionStates[email.id] === '1' && availableLocations.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                            {selectedLocationNames.length > 0 ? (
                                selectedLocationNames.map((name: string, index: number) => (
                                    <Chip
                                        key={index}
                                        label={name}
                                         onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                }}
                                        size="small"
                                        sx={{
                                            backgroundColor: `${COLORS.teal}20`,
                                            color: COLORS.teal,
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No locations selected
                                </Typography>
                            )}  </Box>
                            
                          
                            <IconButton
                              size="small"
                              onClick={() => handleExpandToggle(email.id)}
                              sx={{ 
                                color: COLORS.teal,
                                '&:hover': { backgroundColor: `${COLORS.teal}10` }
                              }}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Box>
                        )}
                        
                        {subscriptionStates[email.id] === '1' && availableLocations.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            No locations available
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    
                    {subscriptionStates[email.id] === '1' && availableLocations.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ p: 0, border: 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: '#fafafa', 
                              borderLeft: `3px solid ${COLORS.teal}`
                            }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Select Locations:
                              </Typography>
                              <Box sx={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: 1 
                              }}>
                                {availableLocations.map((location: any) => {
                                  const isChecked = currentSelectedLocations.includes(String(location.OVID));
                                  
                                  return (
                                    <FormControlLabel
                                      key={location.OVID}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={isChecked}
                                          onChange={() => handleLocationToggle(email.id, location.OVID)}
                                          sx={{ 
                                            color: COLORS.teal,
                                            '&.Mui-checked': { color: COLORS.teal }
                                          }}
                                        />
                                      }
                                      label={
                                        <Typography variant="body2">
                                          {location.name}
                                        </Typography>
                                      }
                                      sx={{ margin: 0 }}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No email subscriptions available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default EmailForm;